import Component from "vue-class-component";
import Vue from "vue";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { EpgItem, MyChannel } from "@/services/apiService/api.types";
import apiService from "@/services/apiService/apiService";
import { formatEpgDates } from "@/helpers/utils";

@Component
export class EpgHelpers extends Vue {
  channelList: MyChannel[] = [];

  //vars for progress bar
  secondsElapsed = 0;
  secondsPerPercent = 0;

  updateActiveEpgItem(activeEpgList: EpgItem[]): EpgItem | undefined {
    dayjs.extend(isBetween);
    const timestamp = dayjs(); //current time adjusted for any possible offset(5s offset = 5 seconds before current time)
    const activeEpgItem = activeEpgList.find((epgItem: EpgItem) => {
      const epgStartTime = dayjs(epgItem.epgStarttime);
      const epgEndTime = dayjs(epgItem.epgEndtime);

      return timestamp.isBetween(epgStartTime, epgEndTime);
    });
    if (activeEpgItem) {
      this.setProgressBarValues(activeEpgItem);
      return activeEpgItem;
    }

    return undefined;
  }

  epgItemStartTime(epgItem?: EpgItem): string {
    if (epgItem && dayjs(epgItem.epgStarttime).isValid()) {
      return dayjs(epgItem.epgStarttime).format("HH:mm");
    }

    if (epgItem && dayjs(epgItem.starttime).isValid()) {
      return dayjs(epgItem.starttime).format("HH:mm");
    }
    return "--";
  }

  epgItemEndTime(epgItem?: EpgItem): string {
    if (epgItem && dayjs(epgItem.epgEndtime).isValid()) {
      return dayjs(epgItem.epgEndtime).format("HH:mm");
    }

    if (epgItem && dayjs(epgItem.endtime).isValid()) {
      return dayjs(epgItem.endtime).format("HH:mm");
    }
    return "--";
  }

  epgItemChannelName(epgItem: EpgItem): string {
    for (let i = this.channelList.length - 1; i >= 0; i--) {
      if (this.channelList[i].id === epgItem.channel_id) {
        return this.channelList[i].name;
      }
    }
    return "No info";
  }

  progressPercentage(epgItem: EpgItem): number {
    if (
      dayjs().isAfter(epgItem.epgEndtime ? epgItem.epgEndtime : epgItem.endtime)
    ) {
      return 100;
    }
    if (
      dayjs().isBefore(
        epgItem.epgStarttime ? epgItem.epgStarttime : epgItem.starttime
      )
    ) {
      return 0;
    }

    return this.liveProgressPercentage;
  }

  get liveProgressPercentage(): number {
    return Math.round(this.secondsElapsed / this.secondsPerPercent);
  }

  handleProgressBar(activeEpgItem: EpgItem): void {
    //epgItemDuration
    const duration = dayjs(activeEpgItem.epgEndtime).diff(
      activeEpgItem.epgStarttime,
      "second"
    );
    //count of seconds from epgItem beginning to now
    this.secondsElapsed = dayjs().diff(activeEpgItem.epgStarttime, "second");
    this.secondsPerPercent = Math.floor(Math.round(duration / 100));
  }

  async fetchChannelEpg(
    channelId: number,
    epgDate?: string
  ): Promise<EpgItem[]> {
    const offset = -(new Date().getTimezoneOffset() / 60);
    const date =
      epgDate && dayjs(epgDate, "YYYY-MM-DD").isValid()
        ? epgDate
        : dayjs().format("YYYY-MM-DD");
    const { items: channelEpg } = await apiService.getChannelEpg(
      channelId,
      date,
      offset
    );

    //returning epg list after formatting dates
    return formatEpgDates<EpgItem>(channelEpg, offset);
  }

  setProgressBarValues(activeEpgItem: EpgItem): void {
    //epgItemDuration
    const duration = dayjs(activeEpgItem.epgEndtime).diff(
      activeEpgItem.epgStarttime,
      "second"
    );
    //count of seconds from epgItem beginning to now
    this.secondsElapsed = dayjs().diff(activeEpgItem.epgStarttime, "second");
    this.secondsPerPercent = Math.floor(Math.round(duration / 100));
  }
}
